import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../utils/apiClient";
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import PageLayout from "../components/admin/PageLayout";

const AddCommittee = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    type: "auction",
    description: "",
  });
  const [saving, setSaving] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const FREE_LIMIT = 2;

  useEffect(() => {
    apiClient
      .get("committee/count")
      .then((res) => {
        if (res.data.success) {
          setLimitReached((res.data.data || 0) >= FREE_LIMIT);
        }
      })
      .catch(() => {});
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (limitReached) {
      showSnackbar(`Free plan allows maximum ${FREE_LIMIT} committees. Subscribe to add more.`, "warning");
      return;
    }
    try {
      setSaving(true);
      const response = await apiClient.post("committee", formData);
      if (response.data.success) {
        showSnackbar("Committee created successfully");
        navigate("/committees");
      } else {
        showSnackbar(response.data.message || "Failed to create committee", "error");
      }
    } catch (error) {
      showSnackbar(
        error.response?.data?.message || "Failed to create committee",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageLayout title="Add Committee" subtitle="Create a new auction committee. Free plan allows up to 2 committees.">
    <Box sx={{ maxWidth: 560 }}>
      {limitReached && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have reached the free limit of {FREE_LIMIT} committees. Subscribe to create more.
        </Alert>
      )}
      <form onSubmit={handleSubmit}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Committee Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            fullWidth
          />
          <FormControl fullWidth required>
            <InputLabel>Type</InputLabel>
            <Select name="type" value={formData.type} onChange={handleChange} label="Type">
              <MenuItem value="auction">Auction</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Description (optional)"
            name="description"
            value={formData.description}
            onChange={handleChange}
            multiline
            rows={3}
            fullWidth
          />
          <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={saving || limitReached}
            >
              {saving ? <CircularProgress size={24} /> : "Create Committee"}
            </Button>
            <Button variant="outlined" onClick={() => navigate("/committees")}>
              Cancel
            </Button>
          </Box>
        </Box>
      </form>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
    </PageLayout>
  );
};

export default AddCommittee;
